import React, { useEffect, useState } from 'react'
import {
  Edit,
  SimpleForm,
  TextInput,
  BooleanInput,
  useTranslate,
  useNotify,
  useRedirect,
  useEditController,
  required,
} from 'react-admin'
import { Grid, Skeleton } from '@mui/material'
import { CardCommon } from 'src/components/uiElements'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { applicationServices } from 'src/services/auth/application/application.service'
import { moduleServices } from 'src/services/auth/module/module.service'
import { clientServices } from 'src/services/auth/client/client.service'
import { CardCrudBottomButtons } from 'src/components/uiElements/CRUD/CardCrudBottomButtons'
import { isJsonValidate } from 'src/helper/validatorJson'
import { SelectInput } from 'src/components/uiElements/fields/SelectInput'

export const ProcessEdit = (props) => {
  const translate = useTranslate()
  const notify = useNotify()
  const redirect = useRedirect()
  const [clients, setClients] = useState([])
  const [loadingClients, setLoadingClients] = useState(true)
  const [applications, setApplications] = useState([])
  const [loadingApplications, setLoadingApplications] = useState(true)
  const [modules, setModules] = useState([])
  const [loadingModules, setLoadingModules] = useState(true)  
  const { record } = useEditController({ resource: 'process', id: props.id })

  useEffect(() => {
    if (record) {
      getAllClients()
      getApplicationsByClient(record.client_id)

      if (record.module) {
        record.application_id = record.module.application_id
        getModulesByApplication(record.module.application_id)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record])

  const getAllClients = async () => {
    setLoadingClients(true)
    try {
      const allClients = await clientServices.getByAll()
      if (allClients) {
        setClients(allClients)
        setLoadingClients(false)
        return
      }
      notify(`applications not found`, { type: 'error' })
      setLoadingClients(false)
    } catch (error) {
      notify(`please, refresh page`, { type: 'error' })
      setLoadingClients(false)
    }
  }

  const getApplicationsByClient = async (idClient) => {
    setLoadingApplications(true)
    if (idClient) {
      try {
        let allApplications = await applicationServices.getApplicationByClient(idClient)
        setApplications(allApplications.map((item) => item.application))
        setLoadingApplications(false)
      } catch (error) {
        notify(`applications not found`, { type: 'error' })
        setLoadingApplications(false)
        return
      }
    }
  }

  const getModulesByApplication = async (idApplication) => {
    setLoadingModules(true)
    if (idApplication) {
      try {
        let allModules = await moduleServices.getModulesByApplications(idApplication)
        setModules(allModules)
        setLoadingModules(false)
      } catch (error) {
        notify(`please, refresh page`, { type: 'error' })
        setModules([])
        setLoadingModules(false)
      }
    }
  }

  const transform = (data) => ({
    short_name: data.short_name,
    long_name: data.long_name,
    text: data.text,
    module_id: data.module_id,
    client_id: data.client_id,
    state: data.state,
    config: data.config,
  })

  const onSuccess = () => {
    notify('ra.notification.updated_success_masculine', {
      type: 'success',
      messageArgs: { element: translate('ra.single_entity.process') }
    })
    redirect('/process')
  }

  const onError = () => {
    notify('ra.notification.updated_error_masculine', {
      type: 'error',
      messageArgs: { element: translate('ra.single_entity.process') }
    })
  }

  return (
    <Edit
      {...props}
      transform={transform}
      mutationMode='pessimistic'
      mutationOptions={{ onSuccess, onError }}
      actions={null}
    >
      <CardCommon title={'Process'}>
        <SimpleForm
          toolbar={<CardCrudBottomButtons btnSave={true} btnDelete={true} fieldsName={['short_name']} genderElement='masculine' />}
        >
          <Grid container spacing={3}>
            <EditInputsWrap label={translate('ra.field.id')}>
              <TextInput disabled source='id' label='' fullWidth />
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.short_name')} required>
              <TextInput source='short_name' label='' fullWidth />
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.long_name')}>
              <TextInput source='long_name' label='' fullWidth validate={required()} />
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.client')} required>
              {loadingClients ? (
                <Skeleton variant='rectangular' width={'100%'} height={40} />
              ) : (
                <SelectInput
                  source='client_id'
                  optionsText={['long_name']}
                  label=''
                  fullWidth
                  options={clients}
                  defaultValue={record.client_id}
                  disabled
                  validate={required()}
                />
              )}
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.application')} required>
              {loadingApplications ? (
                <Skeleton variant='rectangular' width={'100%'} height={40} />
              ) : (
                <SelectInput
                  source='application_id'
                  optionsText={['long_name']}
                  label=''
                  fullWidth
                  options={applications}
                  defaultValue={record.application_id}
                  onChange={(e) => {
                    record.module_id = null
                    getModulesByApplication(e.target.value)
                  }}
                  validate={required()}
                />
              )}
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.module')} required>
              {loadingModules ? (
                <Skeleton variant='rectangular' width={'100%'} height={40} />
              ) : (
                <SelectInput
                  source='module_id'
                  optionsText={['long_name']}
                  options={modules}
                  defaultValue={record.module_id}
                  label=''
                  fullWidth
                  validate={required()}
                />
              )}
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.config')}>
              <TextInput
                source='config'
                label={false}
                multiline
                rows={5}
                fullWidth
                validate={isJsonValidate}
                format={(v) => {
                  return typeof v === 'object' ? JSON.stringify(v) : v
                }}
                parse={(v) => {
                  try {
                    return (v && v.length >= 2) ? JSON.parse(v) : v
                  } catch (e) {
                    return v || undefined
                  }
                }}
              />
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.text')}>
              <TextInput source='text' label='' fullWidth />
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.state')}>
              <BooleanInput label={translate('ra.field.is_active')} source='state' fullWidth />
            </EditInputsWrap>
          </Grid>
        </SimpleForm>
      </CardCommon>
    </Edit>
  )
}
